/**
 * CPT Battery Health Estimation Service
 *
 * Estimates battery State of Health (SOH), Remaining Useful Life (RUL),
 * and related metrics from inputs available via the Tesla Fleet API.
 *
 * This is a CPT estimation model, not an official Tesla diagnostic.
 * Where Tesla Fleet API provides direct battery measurements, those take precedence.
 *
 * Methodology: /report-design/DEV-NOTES.md
 */

export type FactorSource = 'measured' | 'derived' | 'regional_default' | 'fixed_default';

export interface BatteryHealthInput {
  /** Measured SoH from Tesla API (%) */
  measuredSoh: number;

  /** Vehicle model year , used to estimate battery age */
  vehicleYear: number;

  /** Total energy charged across full history (kWh) , from chargingSummary.totalEnergyKwh */
  totalEnergyKwh: number;

  /** Usable battery capacity (kWh) , from batterySummary.batteryCapacityKwh */
  batteryCapacityKwh: number;

  /** Estimated range at current SoH (km) , from batterySummary.estimatedRangeKm */
  estimatedRangeKm: number;

  /** Odometer reading (km) */
  odometerKm: number;

  /** Charge limit SoC (%) , from batterySummary.chargeLimitSoc or charge_state */
  chargeLimitSoc: number;

  /**
   * Vehicle region string (e.g. "EU", "NA", "AP").
   * Used as climate proxy. Historical weather data is not reachable
   * from the Tesla Fleet API , a regional default is the best available approximation.
   */
  region: string;

  /**
   * DC fast charge fraction (0–1).
   * Must be derived from charging session history:
   * sessions where avgPower (usageBase kWh / duration hours) > 25 kW = DC.
   * Pass null when session data is absent , f_dc will be treated as unknown (0, not guessed).
   */
  dcFastChargeFraction: number | null;
}

export interface StressFactors {
  /** DC fast charging exposure (0–1) */
  f_dc: number;
  f_dc_source: FactorSource;

  /** Hot climate exposure (0–1). Regional proxy , real temperature history is unavailable. */
  f_hot: number;
  f_hot_source: FactorSource;

  /** Time spent at high SoC 90–100% (0–1). Derived from charge limit setting. */
  f_highsoc: number;
  f_highsoc_source: FactorSource;

  /** Total stress penalty P = 3·f_dc + 2·f_hot + 1.5·f_highsoc */
  P: number;
}

export interface BatteryHealthResult {
  /** Battery age in years (derived from vehicle year) */
  ageYears: number;

  /** Equivalent Full Cycles = totalEnergyKwh / batteryCapacityKwh */
  efc: number;

  stressFactors: StressFactors;

  /** CPT-estimated SoH using the methodology formula (%) */
  estimatedSoh: number;

  /** Difference between CPT estimate and Tesla-measured SoH (positive = model above measured) */
  modelVariance: number;

  /** Annual degradation rate used for RUL projection (%/yr) , derived from stress level */
  annualDegradationRate: number;

  /** Remaining Useful Life to 70% SoH threshold (years) */
  remainingUsefulLifeYears: number;

  /** Projected odometer km at which 70% SoH floor is reached */
  projectedKmToWarrantyFloor: number;

  /** Back-calculated factory WLTP rated range (km) = estimatedRangeKm / (measuredSoh / 100) */
  ratedRangeKm: number;

  /** Range reduction from factory rating (km) */
  rangeReductionKm: number;

  /** Degradation rate per 1,000 km (%) */
  degradationRatePer1000km: number;
}

const SOH_END_OF_LIFE = 70;

function resolveF_hot(region: string): { value: number; source: FactorSource } {
  const r = region.toUpperCase();
  if (r.startsWith('EU') || r === 'EUROPE') return { value: 0.10, source: 'regional_default' };
  if (r.startsWith('NA') || r.startsWith('US')) return { value: 0.15, source: 'regional_default' };
  if (r.startsWith('AP') || r.startsWith('ME') || r.startsWith('AF')) return { value: 0.20, source: 'regional_default' };
  return { value: 0.12, source: 'regional_default' };
}

function resolveF_highsoc(chargeLimitSoc: number): { value: number; source: FactorSource } {
  if (chargeLimitSoc > 90) return { value: 0.30, source: 'derived' };
  if (chargeLimitSoc > 80) return { value: 0.10, source: 'derived' };
  return { value: 0.00, source: 'derived' };
}

function resolveF_dc(fraction: number | null): { value: number; source: FactorSource } {
  if (typeof fraction === 'number' && fraction >= 0 && fraction <= 1) {
    return { value: fraction, source: 'derived' };
  }
  // No session data available , treat as unknown, not guessed
  return { value: 0, source: 'fixed_default' };
}

export function estimateBatteryHealth(input: BatteryHealthInput): BatteryHealthResult {
  const currentYear = new Date().getFullYear();
  const ageYears = Math.max(1, currentYear - input.vehicleYear);

  const efc = input.batteryCapacityKwh > 0
    ? input.totalEnergyKwh / input.batteryCapacityKwh
    : 0;

  const hotFactor = resolveF_hot(input.region);
  const highsocFactor = resolveF_highsoc(input.chargeLimitSoc);
  const dcFactor = resolveF_dc(input.dcFastChargeFraction);

  const P = 3 * dcFactor.value + 2 * hotFactor.value + 1.5 * highsocFactor.value;

  const stressFactors: StressFactors = {
    f_dc: dcFactor.value,
    f_dc_source: dcFactor.source,
    f_hot: hotFactor.value,
    f_hot_source: hotFactor.source,
    f_highsoc: highsocFactor.value,
    f_highsoc_source: highsocFactor.source,
    P,
  };

  const estimatedSoh = 100 - (1.2 * ageYears) - (0.006 * efc) - P;
  const modelVariance = estimatedSoh - input.measuredSoh;

  // Annual degradation: derived from stress level
  const annualDegradationRate = P < 1.5 ? 1.5 : P < 3.0 ? 2.0 : 2.5;

  const remainingUsefulLifeYears = annualDegradationRate > 0
    ? (input.measuredSoh - SOH_END_OF_LIFE) / annualDegradationRate
    : 0;

  // From DEV-NOTES: projected_km = current_km * (current_soh - 70) / degradation_rate_per_km
  // where degradation_rate_per_km = (100 - soh) / current_km
  // simplifies to: current_km * (soh - 70) / (100 - soh)
  const degradationRatePer1000km = input.odometerKm > 0
    ? (100 - input.measuredSoh) / input.odometerKm * 1000
    : 0;

  const projectedKmToWarrantyFloor = (100 - input.measuredSoh) > 0 && input.odometerKm > 0
    ? input.odometerKm * (input.measuredSoh - SOH_END_OF_LIFE) / (100 - input.measuredSoh)
    : 0;

  const ratedRangeKm = input.measuredSoh > 0
    ? input.estimatedRangeKm / (input.measuredSoh / 100)
    : input.estimatedRangeKm;

  const rangeReductionKm = ratedRangeKm - input.estimatedRangeKm;

  return {
    ageYears,
    efc: Math.round(efc),
    stressFactors,
    estimatedSoh: parseFloat(estimatedSoh.toFixed(1)),
    modelVariance: parseFloat(modelVariance.toFixed(1)),
    annualDegradationRate,
    remainingUsefulLifeYears: parseFloat(remainingUsefulLifeYears.toFixed(1)),
    projectedKmToWarrantyFloor: Math.round(projectedKmToWarrantyFloor),
    ratedRangeKm: Math.round(ratedRangeKm),
    rangeReductionKm: Math.round(rangeReductionKm),
    degradationRatePer1000km: parseFloat(degradationRatePer1000km.toFixed(3)),
  };
}
